import { fetchClient } from "@/api/api";
import type {
	allBuyersInput,
	allBuyersOutput,
	allProxiesInput,
	allProxiesOutput,
	allSellersInput,
	allSellersOutput,
	allSenderGroupNamesInput,
	allSenderGroupNamesOutput,
	getIdFromNameInput,
	getIdFromNameOutput,
	getStatusInformationOutput,
} from "@/api/custom_types";
import type { CustomQueryHook } from "@/utils/react-query";

import { useQuery } from "@tanstack/react-query";
import { GENERAL_SERVICE_KEYS } from "./keys";

const useGetAllBuyers: CustomQueryHook<allBuyersOutput, allBuyersInput> = (
	payload,
	options,
) => {
	return useQuery({
		...options,
		queryKey: GENERAL_SERVICE_KEYS.useGetAllBuyers(payload),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/marketplace/dashboard/buyers/",
				{ params: { query: payload?.query }, throwOnError: true },
			);
			if (error) throw error;
			return data;
		},
	});
};

const useGetAllSellers: CustomQueryHook<allSellersOutput, allSellersInput> = (
	payload,
	options,
) => {
	return useQuery({
		...options,
		queryKey: GENERAL_SERVICE_KEYS.useGetAllSellers(payload),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/marketplace/dashboard/sellers/",
				{ params: { query: payload?.query }, throwOnError: true },
			);
			if (error) throw error;
			return data;
		},
	});
};

const useGetAllGroups: CustomQueryHook<
	allSenderGroupNamesOutput,
	allSenderGroupNamesInput
> = (payload, options) => {
	return useQuery({
		...options,
		queryKey: GENERAL_SERVICE_KEYS.useGetAllGroups(payload),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/marketplace/dashboard/sender-group-names/",
				{ params: { query: payload?.query }, throwOnError: true },
			);
			if (error) throw error;
			return data;
		},
	});
};

const useGetAllProxies: CustomQueryHook<allProxiesOutput, allProxiesInput> = (
	payload,
	options,
) => {
	return useQuery({
		...options,
		queryKey: GENERAL_SERVICE_KEYS.useGetAllProxies(payload),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/marketplace/dashboard/proxies/",
				{ params: { query: payload?.query }, throwOnError: true },
			);
			if (error) throw error;
			return data;
		},
	});
};

const useGetIdFromName: CustomQueryHook<
	getIdFromNameOutput,
	getIdFromNameInput
> = (payload, options) => {
	return useQuery({
		...options,
		queryKey: GENERAL_SERVICE_KEYS.useGetIdFromName(payload),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/marketplace/users/name-lookup",
				{ params: { query: payload?.query }, throwOnError: true },
			);
			if (error) throw error;
			return data;
		},
	});
};

const useGetStatusInformation: CustomQueryHook<
	getStatusInformationOutput,
	void
> = (options) => {
	return useQuery({
		...options,
		queryKey: GENERAL_SERVICE_KEYS.useOriginStatus("useOriginStatus"),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET("/v1/marketplace/status/", {
				throwOnError: true,
			});
			if (error) throw error;
			return data;
		},
		staleTime: 1 * 60 * 60 * 1_000, // 1 hour
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
	});
};

export {
	useGetAllGroups,
	useGetAllSellers,
	useGetAllBuyers,
	useGetAllProxies,
	useGetIdFromName,
	useGetStatusInformation,
};
